import { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Percent,
  Calendar,
  AlertCircle,
  X,
} from 'lucide-react';
import { createTaxCode, deleteTaxCode, getTaxCodes, updateTaxCode } from '../../utils/taxCodeApi';
import { ApiValidationError } from '../../types/auth';
import type { TaxCode } from '../../types/taxCode';
import { formatTaxCodeLabel } from '../../types/taxCode';
import { toast, toastApiError } from '../../utils/toast';

interface TaxCodeFormData {
  name: string;
  rate_percent: string;
  is_taxable: boolean;
  active: boolean;
}

interface FieldErrors {
  [key: string]: string[] | undefined;
}

type StatusFilter = 'all' | 'active' | 'inactive';
type TaxableFilter = 'all' | 'taxable' | 'exempt';

const canManageTaxCodes = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteTaxCodes = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const defaultFormData = (): TaxCodeFormData => ({
  name: '',
  rate_percent: '',
  is_taxable: true,
  active: true,
});

const taxCodeToForm = (code: TaxCode): TaxCodeFormData => ({
  name: code.name,
  rate_percent: code.rate_percent,
  is_taxable: code.is_taxable,
  active: code.active,
});

const TaxCodesPanel = () => {
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [taxableFilter, setTaxableFilter] = useState<TaxableFilter>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCode, setSelectedCode] = useState<TaxCode | null>(null);
  const [formData, setFormData] = useState<TaxCodeFormData>(defaultFormData);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [modalError, setModalError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canEdit = canManageTaxCodes();
  const canDelete = canDeleteTaxCodes();

  useEffect(() => {
    fetchTaxCodes();
  }, [statusFilter, taxableFilter]);

  const fetchTaxCodes = async () => {
    try {
      setLoading(true);
      const params: { active?: boolean; is_taxable?: boolean } = {};
      if (statusFilter === 'active') params.active = true;
      if (statusFilter === 'inactive') params.active = false;
      if (taxableFilter === 'taxable') params.is_taxable = true;
      if (taxableFilter === 'exempt') params.is_taxable = false;

      const data = await getTaxCodes(params);
      setTaxCodes(data);
    } catch (error) {
      console.error('Error fetching tax codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(defaultFormData());
    setFieldErrors({});
    setModalError(null);
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    if (!formData.name.trim()) {
      errors.name = ['Name is required'];
    }
    const rate = parseFloat(formData.rate_percent);
    if (!formData.rate_percent.trim()) {
      errors.rate_percent = ['Rate is required'];
    } else if (!Number.isFinite(rate) || rate < 0 || rate > 9999.99) {
      errors.rate_percent = ['Rate must be between 0 and 9999.99'];
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      setModalError(null);
      setFieldErrors({});

      await createTaxCode({
        name: formData.name.trim(),
        rate_percent: parseFloat(formData.rate_percent),
        is_taxable: formData.is_taxable,
        active: formData.active,
      });

      setShowCreateModal(false);
      resetForm();
      await fetchTaxCodes();
      toast.success('Tax code created successfully.');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to create tax code');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedCode || !validateForm()) return;

    try {
      setIsSubmitting(true);
      setModalError(null);
      setFieldErrors({});

      await updateTaxCode(selectedCode.id, {
        name: formData.name.trim(),
        rate_percent: parseFloat(formData.rate_percent),
        is_taxable: formData.is_taxable,
        active: formData.active,
      });

      setShowEditModal(false);
      setSelectedCode(null);
      resetForm();
      await fetchTaxCodes();
      toast.success('Tax code updated successfully.');
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setFieldErrors(err.errors);
        setModalError(err.message);
      } else {
        toastApiError(err, 'Failed to update tax code');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCode) return;

    try {
      await deleteTaxCode(selectedCode.id);
      setShowDeleteModal(false);
      setSelectedCode(null);
      await fetchTaxCodes();
      toast.success('Tax code deleted successfully.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete tax code');
      setShowDeleteModal(false);
    }
  };

  const openEditModal = (code: TaxCode) => {
    setSelectedCode(code);
    setFormData(taxCodeToForm(code));
    setFieldErrors({});
    setModalError(null);
    setShowEditModal(true);
  };

  const openDeleteModal = (code: TaxCode) => {
    setSelectedCode(code);
    setShowDeleteModal(true);
  };

  const filteredCodes = taxCodes.filter((code) =>
    code.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const fieldError = (field: string) => fieldErrors[field]?.[0];

  const renderFormFields = (idPrefix: string) => (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-gray-700 mb-1">
          Name
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          maxLength={50}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400 ${
            fieldError('name') ? 'border-red-300' : 'border-gray-200'
          }`}
          placeholder="e.g. VAT18"
        />
        {fieldError('name') && <p className="mt-1 text-xs text-red-600">{fieldError('name')}</p>}
      </div>

      <div>
        <label
          htmlFor={`${idPrefix}-rate`}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Rate (%)
        </label>
        <input
          id={`${idPrefix}-rate`}
          type="number"
          min={0}
          max={9999.99}
          step={0.01}
          value={formData.rate_percent}
          onChange={(e) => setFormData({ ...formData, rate_percent: e.target.value })}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400 ${
            fieldError('rate_percent') ? 'border-red-300' : 'border-gray-200'
          }`}
          placeholder="18"
        />
        {fieldError('rate_percent') && (
          <p className="mt-1 text-xs text-red-600">{fieldError('rate_percent')}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          id={`${idPrefix}-taxable`}
          type="checkbox"
          checked={formData.is_taxable}
          onChange={(e) => setFormData({ ...formData, is_taxable: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-slate-800 focus:ring-slate-800"
        />
        <label htmlFor={`${idPrefix}-taxable`} className="text-sm text-gray-700">
          Taxable (uncheck for exempt / zero-rated codes)
        </label>
      </div>

      <div className="flex items-center gap-3">
        <input
          id={`${idPrefix}-active`}
          type="checkbox"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-slate-800 focus:ring-slate-800"
        />
        <label htmlFor={`${idPrefix}-active`} className="text-sm text-gray-700">
          Active
        </label>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {!canEdit && (
        <div className="mb-6 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span>You can view tax codes here. Only administrators and accountants can make changes.</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <p className="text-sm text-gray-500">
          Manage VAT rates and exemptions used on invoice and bill line items.
        </p>
        {canEdit && (
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-800"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Tax Code
          </button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800/20 focus:border-slate-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-[140px]"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <select
          value={taxableFilter}
          onChange={(e) => setTaxableFilter(e.target.value as TaxableFilter)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-[140px]"
        >
          <option value="all">All types</option>
          <option value="taxable">Taxable</option>
          <option value="exempt">Exempt / zero-rated</option>
        </select>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                {canEdit && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCodes.map((code) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Percent className="h-4 w-4 text-slate-700" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{code.name}</div>
                        <div className="text-xs text-gray-500">{formatTaxCodeLabel(code)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 tabular-nums">
                    {code.rate_percent}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        code.is_taxable
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {code.is_taxable ? 'Taxable' : 'Exempt'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        code.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {code.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {formatDate(code.created_at)}
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openEditModal(code)}
                          className="text-slate-700 hover:text-slate-900 p-1 rounded hover:bg-slate-50"
                          title="Edit tax code"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => openDeleteModal(code)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete tax code"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCodes.length === 0 && (
          <div className="text-center py-12">
            <Percent className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No tax codes found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search or filters.' : 'Add a tax code to get started.'}
            </p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Tax Code</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {modalError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {modalError}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreate();
              }}
            >
              {renderFormFields('create')}
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Tax Code</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedCode(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {modalError && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {modalError}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdate();
              }}
            >
              {renderFormFields('edit')}
              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedCode(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && selectedCode && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Tax Code</h3>
              <p className="mt-2 px-4 text-sm text-gray-500">
                Are you sure you want to delete <strong>{selectedCode.name}</strong>? Existing
                invoice and bill lines that reference this code will have their tax code cleared.
              </p>
              <div className="flex justify-center space-x-3 px-4 py-6">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxCodesPanel;
